import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { manualSectionPath } from '../../../helpers/manualPath';
import ManualSection from '../ManualSection';
import './reputation-manual.scss';

/**
 * `/readme/reputation` manual content (TRIL-427), on the TRIL-424 framework.
 *
 * HARD content rule (anti-gaming): principles, not the formula. Explain what
 * each dimension *reflects* and that reputation is earned over time through
 * genuine contribution — never the exact weights, the URG scoring pipeline,
 * thresholds, or anything that reads as "do X to inflate dimension Y".
 *
 * The eight dimensions mirror `helpers/reputationView.ts` DIMENSION_META
 * (Engagement, Consistency, Diversity & Inclusivity, Behavior, Community
 * Impact, Content Quality, Innovativeness, Expertise) — labels only; the
 * "what it reflects" copy is conceptual.
 */
export default function ReputationManual() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    el?.scrollIntoView({
      behavior: reduce ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [hash]);

  return (
    <div className="REP__MANUAL">
      <p className="MANUAL__LEAD">
        Your reputation is a running measure of how you show up here over time —
        the quality of what you publish and how you treat the community.
        It&apos;s <strong>earned, not given</strong>, and it moves slowly.
      </p>

      <div className="REP__CAVEAT" id="how-it-moves">
        <span className="REP__CAVEAT__MK">How it moves</span>
        <p>
          Reputation reflects <b>patterns, not moments.</b> A single post rarely
          shifts it much, and your <b>earned</b> reputation can&apos;t be bought
          or gamed with one big swing — it builds (and recovers) gradually as a
          body of work and conduct accumulates. That&apos;s deliberate: it&apos;s
          meant to be hard to fake.
        </p>
      </div>

      <section className="REP__BLOCK" id="what-we-value">
        <h2>What it rewards</h2>
        <p className="REP__SUB">
          Genuine, consistent, high-quality contribution to a healthy community.
          The eight dimensions below each capture one facet of that — no single
          one is the whole score, and you don&apos;t need to max out every one.
        </p>
      </section>

      <section className="REP__BLOCK" id="dimensions">
        <h2>The eight dimensions</h2>
        <p className="REP__SUB">
          Open any one for what it reflects. On your own reputation page, each
          also shows the evidence behind it — visible only to you.
        </p>

        <ManualSection id="engagement" title="Engagement" defaultOpen>
          <p>
            How much genuine back-and-forth your work creates — people reading,
            replying, and coming back. It&apos;s about{' '}
            <b>resonance, not raw volume</b>: a pile of low-effort posts
            doesn&apos;t move it the way work people actually engage with does.
          </p>
        </ManualSection>

        <ManualSection id="consistency" title="Consistency">
          <p>
            Showing up steadily over time rather than in a single burst.
            Reputation is a long game — a dependable track record counts for
            more than one spike followed by silence.
          </p>
        </ManualSection>

        <ManualSection id="diversity" title="Diversity &amp; Inclusivity">
          <p>
            Writing that welcomes a broad range of readers and perspectives, and
            engaging across the community rather than only within a narrow
            circle.
          </p>
        </ManualSection>

        <ManualSection id="behavior" title="Behavior">
          <p>
            How you conduct yourself — constructive interactions, playing by the
            rules, and treating others well. This is where moderation issues and
            bad-faith conduct weigh against you.
          </p>
        </ManualSection>

        <ManualSection id="community-impact" title="Community Impact">
          <p>
            The positive difference your presence makes — lifting others,
            mentoring (including vouching well), and starting conversations
            worth having. Good citizenship shows up here.
          </p>
        </ManualSection>

        <ManualSection id="content-quality" title="Content Quality">
          <p>
            The substance and craft of what you publish, read across your body
            of work. It rewards writing that holds up over time — not one-off
            polish on a single piece.
          </p>
        </ManualSection>

        <ManualSection id="innovativeness" title="Innovativeness">
          <p>
            Bringing something fresh — original angles, ideas, or formats —
            rather than echoing what&apos;s already there.
          </p>
        </ManualSection>

        <ManualSection id="expertise" title="Expertise">
          <p>
            Demonstrated depth in the topics you write about — shown through a
            body of credible, substantive work and your verified credentials,
            not merely asserted on a profile.
          </p>
        </ManualSection>
      </section>

      <section className="REP__BLOCK" id="raising-it">
        <h2>How to raise it</h2>
        <p className="REP__SUB">
          Keep contributing honestly and it compounds — there&apos;s no shortcut
          worth taking. Chasing a single dimension tends not to work, because
          the dimensions reinforce each other and the whole thing rewards
          patterns over stunts. Write well, treat people well, and stick around.
        </p>
      </section>

      <div className="REP__NOTE" id="displayed-reach">
        <span className="REP__NOTE__MK">One nuance</span>
        <p>
          The number that drives your <em>reach</em> can be temporarily higher
          than your earned score if someone has vouched for you — that boost is
          borrowed and fades. See the{' '}
          <Link to={manualSectionPath('vouching')}>vouching guide</Link> for how
          that works. Your <em>earned</em> reputation is the durable part.
        </p>
      </div>

      <div className="REP__NOTE REP__NOTE--formula" id="why-no-formula">
        <span className="REP__NOTE__MK">
          Why we don&apos;t show the formula
        </span>
        <p>
          We don&apos;t publish the exact weights or thresholds behind the
          dimensions. A published formula becomes a recipe to game — and a
          reputation earned by gaming isn&apos;t worth much. Contribute for
          real; the score follows.
        </p>
      </div>
    </div>
  );
}
